import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { User } from 'firebase/auth';
import { isFirebaseConfigured } from '@/lib/firebase';
import {
  getAgentProfile,
  isAdmin,
  signInAgent,
  signOutUser,
  subscribeAuth,
} from '@/services/authService';
import { startPresence, updatePresenceStatus } from '@/services/presenceService';
import { requestBrowserPermission } from '@/services/notificationService';
import type { SupportAgent } from '@/types/support';

interface SupportAuthContextValue {
  user: User | null;
  agent: SupportAgent | null;
  admin: boolean;
  loading: boolean;
  configured: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshAgent: () => Promise<void>;
}

const SupportAuthContext = createContext<SupportAuthContextValue | null>(null);

export function SupportAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [agent, setAgent] = useState<SupportAgent | null>(null);
  const [admin, setAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const configured = isFirebaseConfigured();

  useEffect(() => {
    if (!configured) {
      setLoading(false);
      return;
    }
    return subscribeAuth(async (u) => {
      setUser(u);
      if (!u || u.isAnonymous) {
        setAgent(null);
        setAdmin(false);
        setLoading(false);
        return;
      }
      const [profile, adminFlag] = await Promise.all([getAgentProfile(u.uid), isAdmin(u.uid)]);
      setAgent(profile);
      setAdmin(adminFlag);
      setLoading(false);
    });
  }, [configured]);

  useEffect(() => {
    if (!user || user.isAnonymous || (!agent?.active && !admin)) return;
    let stop: (() => void) | undefined;
    void startPresence({
      uid: user.uid,
      role: admin && !agent ? 'admin' : 'agent',
      status: 'online',
    }).then((s) => {
      stop = s;
    });
    void requestBrowserPermission();
    return () => {
      stop?.();
      void updatePresenceStatus(user.uid, 'offline');
    };
  }, [user?.uid, agent?.active, admin]);

  const value = useMemo<SupportAuthContextValue>(
    () => ({
      user,
      agent,
      admin,
      loading,
      configured,
      signIn: async (email, password) => {
        await signInAgent(email, password);
      },
      signOut: async () => {
        if (user) await updatePresenceStatus(user.uid, 'offline');
        await signOutUser();
      },
      refreshAgent: async () => {
        if (!user) return;
        const [profile, adminFlag] = await Promise.all([getAgentProfile(user.uid), isAdmin(user.uid)]);
        setAgent(profile);
        setAdmin(adminFlag);
      },
    }),
    [user, agent, admin, loading, configured],
  );

  return <SupportAuthContext.Provider value={value}>{children}</SupportAuthContext.Provider>;
}

export function useSupportAuth() {
  const ctx = useContext(SupportAuthContext);
  if (!ctx) throw new Error('useSupportAuth must be used within SupportAuthProvider');
  return ctx;
}
